/**
 * updateSphereType.ts
 *
 * The composer's one write call (EAINT-895 Phase 3). Kept out of `schemaApi.ts`,
 * whose header explicitly reserves that file for reads. Always opts into
 * `mode: 'draft'` — the composer accumulates edits on the caller's working
 * draft and never publishes on write (spec B2b); publishing is a separate,
 * explicit action (Phase 4).
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { ArcDefault, SchemaProperty, SchemaPerspective, SchemaRelationship } from 'types/schema';
import type { BusinessRule } from 'types/businessRules';
import type { AspectRef } from 'types/aspect';

export interface UpdateSphereTypeFields {
  name?: string;
  description?: string;
  /**
   * The type's mark — an Iconify `set:name` or a letter, NOT a PrimeIcon class
   * (spec `Renders as:`). Already in `update-schema`'s accepted body; the
   * composer just never sent it until the prototype's Glyph field landed.
   */
  glyph?: string;
  /**
   * The mark's colour — a design-system token, a brand hue, or a literal hex
   * (`types/glyph`). `update-schema` has accepted `glyphColor` since the field
   * existed; the composer only gained a control for it with the glyph picker's
   * Colour tab, and sending the glyph without it would silently drop the colour
   * an author just chose.
   */
  glyphColor?: import('types/glyph').GlyphColor | null;
  useCaseTags?: string[];
  isHidden?: boolean;
  /**
   * REPLACE semantics on the type's OWN (non-inherited, non-aspect-contributed)
   * property set (spec § API Design). The caller must supply the complete own
   * set — this is never a partial patch.
   */
  properties?: SchemaProperty[];
  /** REPLACE semantics on the type's OWN (non-inherited) relationship set (EAINT-895 Phase 3c). */
  relationships?: SchemaRelationship[];
  /** REPLACE semantics on the type's OWN (non-inherited) perspective overrides (EAINT-895 Phase 3c). */
  perspectives?: SchemaPerspective[];
  /**
   * The three perspective-INHERITANCE levers (EAINT-897). Set-only; `null` removes
   * the key so the type follows its ancestors again. Not part of `perspectives`,
   * which carries only this type's own declarations — these govern which INHERITED
   * perspectives arrive at all.
   */
  suppressPerspectives?: string[] | null;
  inheritPerspectives?: string[] | null;
  overridePerspectiveInheritance?: boolean | null;
  /** REPLACE semantics, like relationships (EAINT-895 B2 / Phase 3c). */
  businessRules?: BusinessRule[];
  /** Child schema fully replaces the parent's list — REPLACE semantics (EAINT-895 Phase 3c). */
  /**
   * REPLACE semantics with `null` as a third instruction: an array declares this
   * type's own list (`[]` = spheres start with no arc at all), `null` removes the
   * key so the type follows its ancestor chain again.
   */
  defaultArcs?: ArcDefault[] | null;
  /**
   * The three Advanced policy defaults (EAINT-897 C6/C7). Scalar-only writes:
   * `attachmentsEnabled` patches `attachments.enabled` and leaves `allowedTypes`
   * untouched server-side, `defaultPrivate` sets the `is_private` a new sphere of
   * this type starts at, and `conversationsEnabled` the comment thread. All three
   * are child-wins wholesale on merge — never per-field inheritance.
   */
  attachmentsEnabled?: boolean;
  defaultPrivate?: boolean;
  conversationsEnabled?: boolean;
  /** REPLACE semantics on the type's composed aspect set — attach/detach/pin all resend the full array (EAINT-895 Phase 3d). Sealed-guarded server-side (B2a). */
  aspects?: AspectRef[];
}

export type UpdateSphereTypeOutcome = 'saved' | 'conflict';

function statusOf(err: unknown): number | undefined {
  const anyErr = err as { response?: { status?: number }; status?: number } | undefined;
  return anyErr?.response?.status ?? anyErr?.status;
}

/**
 * Patches a single field group onto the schema's working draft, guarded by
 * optimistic concurrency (EAINT-895 B5). The tenant is the active
 * organization the server reads from the validated header.
 *
 * A 409 (stale `If-Match`) resolves to `'conflict'` rather than throwing —
 * that is an expected outcome the caller must render (spec
 * `staleEditSurfacesConflictNotClobber`), not an exceptional failure. Every
 * other error rethrows for the caller's generic error handling.
 */
export async function updateSphereType(
  schemaClient: ApiClient,
  systemName: string,
  fields: UpdateSphereTypeFields,
  ifMatchVersionId: string
): Promise<UpdateSphereTypeOutcome> {
  try {
    await schemaClient.post(
      '/update-schema',
      { systemName, mode: 'draft', ...fields },
      { headers: { 'If-Match': ifMatchVersionId } }
    );
    return 'saved';
  } catch (err) {
    if (statusOf(err) === 409) return 'conflict';
    throw err;
  }
}
